import { useState, useEffect, ReactElement } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Survey } from '../../../redux/admin/survey/survey.types';
import { useUpdateAdminSurveyMutation } from 'src/redux/admin/survey/survey.endpoints';
import { SurveyStatus } from 'src/redux/survey/survey.types';
import { FormEvent } from 'primereact/ts-helpers';

interface SurveyEditBlockProps {
  survey: Survey;
}

const statusOptions = Object.values(SurveyStatus).map((status) => ({ label: status, value: status }));

const SurveyEditBlock = ({ survey }: SurveyEditBlockProps): ReactElement => {
  const [isPublished, setIsPublished] = useState(survey?.isPublished);
  const [surveyStatus, setSurveyStatus] = useState(survey?.surveyStatus);
  const [startDate, setStartDate] = useState(survey?.startDate);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);

  const [editSurvey, { isSuccess: editSurveySuccess, error: editSurveyError }] = useUpdateAdminSurveyMutation();

  useEffect(() => {
    if (editSurveySuccess) {
      setDialogTitle('Success');
      setDialogMessage('Survey was updated');
      setDialogVisible(true);
    }
  }, [editSurveySuccess]);

  useEffect(() => {
    if (editSurveyError) {
      setDialogTitle('Error updating survey');
      setDialogMessage(editSurveyError.message ?? 'An error occurred');
      setDialogVisible(true);
    }
  }, [editSurveyError]);

  const handleSurveyUpdate = (event: CheckboxChangeEvent | DropdownChangeEvent | FormEvent) => {
    if (typeof event === 'object' && 'checked' in event) {
      setIsPublished(event.checked ?? false);
      editSurvey({ ext_survey_id: survey.extSurveyId, is_published: event.checked ?? false });
    } else if (typeof event === 'object' && 'value' in event && typeof event.value === 'string') {
      setSurveyStatus(event.value);
      editSurvey({ ext_survey_id: survey.extSurveyId, survey_status: event.value as SurveyStatus });
    } else {
      setStartDate(event.value.toString());
      editSurvey({ ext_survey_id: survey.extSurveyId, start_date: event.value });
    }
  };

  return (
    <>
      <div className="w-full mv-3">
        <div className="text-md font-bold mb-1">
          Edit Survey
        </div>
        <div className="flex flex-col w-fit gap-2 border rounded-md bg-slate-100 p-2">
          <div className="flex align-items-center m-1">
            <Checkbox
              name="Published"
              onChange={handleSurveyUpdate}
              checked={isPublished ?? false}
            />
            <label
              htmlFor="published"
              className="ml-2"
            >
              Published
            </label>
          </div>
          <div className="flex flex-col align-items-center m-1">
            <label htmlFor="status">
              Update Survey Status
            </label>
            <Dropdown
              value={surveyStatus}
              onChange={handleSurveyUpdate}
              options={statusOptions}
            />
          </div>

          <div className="flex flex-col align-items-center m-1">
            <label htmlFor="status">
              Update Start Date
            </label>
            <Calendar
              value={new Date(startDate ?? '')}
              onChange={handleSurveyUpdate}
            />
          </div>
        </div>
      </div >
      <Dialog
        header={dialogTitle}
        visible={dialogVisible}
        style={{ width: '50vw' }}
        onHide={() => setDialogVisible(false)}
      >
        <p className="m-0">
          {dialogMessage}
        </p>
      </Dialog>
    </>
  );
};

export default SurveyEditBlock;
